import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { api, buildUrl } from "@shared/routes";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

export function useConversations(userId?: string) {
  return useQuery({
    queryKey: [api.messages.conversations.path, userId || "anon"],
    queryFn: async () => {
      const res = await fetch(api.messages.conversations.path, { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error("Failed to fetch conversations");
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!userId,
    refetchInterval: 5000,
  });
}

export function useConversation(id: number, userId?: string) {
  return useQuery({
    queryKey: [api.messages.getConversation.path, id, userId || "anon"],
    queryFn: async () => {
      const url = buildUrl(api.messages.getConversation.path, { id });
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401) throw new Error("Unauthorized");
      if (res.status === 403) throw new Error("Forbidden");
      if (!res.ok) throw new Error("Failed to load conversation");
      const data = await res.json();
      if (!data || typeof data !== "object") throw new Error("Invalid response");
      return data;
    },
    enabled: id > 0 && !!userId,
    refetchInterval: 5000,
    retry: 1,
  });
}

export function useUnreadCount(userId?: string) {
  return useQuery({
    queryKey: [api.messages.unreadCount.path, userId || "anon"],
    queryFn: async () => {
      const res = await fetch(api.messages.unreadCount.path, { credentials: "include" });
      if (res.status === 401) return { count: 0 };
      if (!res.ok) return { count: 0 };
      const data = await res.json();
      return { count: Number((data as any)?.count ?? 0) };
    },
    refetchInterval: 10000,
    enabled: !!userId,
  });
}

export function useStartConversation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async ({ listingId, message }: { listingId: number; message: string }) => {
      const res = await fetch(api.messages.startConversation.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listingId, message }),
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to send message");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('/api/messages');
      }});
      toast({ title: t("toast.messageSentSuccess") });
    },
    onError: (error: Error) => {
      toast({ title: error.message || t("toast.messageSentError"), variant: "destructive" });
    }
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, content }: { 
      conversationId: number; 
      content: string 
    }) => {
      const res = await fetch(api.messages.send.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId, content }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to send message");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('/api/messages');
      }});
    },
  });
}

export function useNewMessageNotification(userId?: string) {
  // Polling fallback — keeps prevCount ref in sync.
  // Toast notifications are handled by SocketContext (real-time WS) to avoid duplicates.
  const { data: unread } = useUnreadCount(userId);
  const prevCountRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (unread === undefined) return;
    const currentCount = unread.count || 0;
    if (!isInitializedRef.current) {
      prevCountRef.current = currentCount;
      isInitializedRef.current = true;
    } else {
      prevCountRef.current = currentCount;
    }
  }, [unread]);
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: number) => {
      const url = buildUrl(api.messages.markRead.path, { id: conversationId });
      const res = await fetch(url, { method: "PUT", credentials: "include" });
      if (!res.ok) throw new Error("Failed to mark as read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('/api/messages');
      }});
    },
  });
}
